import React, { useMemo, useState } from "react";
import { useAppContext } from "../../../contexts/AppContext";
import {
  labelIsAirlineCode,
  resolveReviewAirlineIdentity,
} from "../review/resolveReviewAirlineIdentity";
import "./StickyBookingSummary.css";

function MoneyRow({ label, value, secondary = null, tone = "default", ltrValue = true }) {
  return (
    <div className={`fe2-sticky__row fe2-sticky__row--${tone}`}>
      <div className="fe2-sticky__row-text">
        <span className="fe2-sticky__row-label">{label}</span>
        {secondary ? (
          <span className="fe2-sticky__row-secondary">{secondary}</span>
        ) : null}
      </div>
      <span className={ltrValue ? "fe2-sticky__row-value fe2-ltr" : "fe2-sticky__row-value"}>
        {value}
      </span>
    </div>
  );
}

/**
 * Optional expandable baggage detail by passenger / segment.
 * Renders ViewModel only — no pricing math.
 */
function BaggageBreakdown({ breakdown }) {
  const groups = breakdown?.groups || [];
  const [open, setOpen] = useState(breakdown?.defaultExpanded !== false);

  if (!groups.length) return null;

  return (
    <div className="fe2-sticky__bag-breakdown">
      <button
        type="button"
        className="fe2-sticky__bag-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open
          ? breakdown?.collapseLabel || "הסתר פירוט"
          : breakdown?.expandLabel || "פירוט לפי נוסע"}
      </button>
      {open ? (
        <div className="fe2-sticky__bag-groups">
          {groups.map((group) => (
            <div
              key={group.passengerName || group.id}
              className="fe2-sticky__bag-group"
            >
              {group.passengerName ? (
                <p className="fe2-sticky__bag-pax fe2-ltr">{group.passengerName}</p>
              ) : null}
              {(group.lines || []).map((line) => (
                <div
                  key={`${group.passengerName}-${line.label}-${line.amountLabel}`}
                  className="fe2-sticky__bag-line"
                >
                  <span className="fe2-sticky__bag-line-label">{line.label}</span>
                  <span className="fe2-sticky__bag-line-value fe2-ltr">
                    {line.amountLabel}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Greenfield handling-fee control — data/callback contract only.
 * Must not import HandlingFeePicker.
 */
function HandlingFeeControls({ handlingFee, onHandlingFeeChange }) {
  if (!handlingFee?.visible || typeof onHandlingFeeChange !== "function") {
    return null;
  }
  const type = handlingFee.type === "%" ? "%" : "+";
  const value = Number.isFinite(Number(handlingFee.value))
    ? Number(handlingFee.value)
    : 0;
  const options = Array.isArray(handlingFee.options) ? handlingFee.options : [];

  return (
    <section className="fe2-sticky__section fe2-sticky__section--fee" aria-label="עמלת טיפול">
      <h3 className="fe2-sticky__section-title">עמלת טיפול</h3>
      <div className="fe2-sticky__fee-row">
        <div className="fe2-sticky__fee-types" role="group" aria-label="סוג עמלה">
          {options.map((opt) => (
            <button
              key={opt.type}
              type="button"
              className={
                type === opt.type
                  ? "fe2-sticky__fee-type fe2-sticky__fee-type--active"
                  : "fe2-sticky__fee-type"
              }
              onClick={() => onHandlingFeeChange({ type: opt.type, value })}
            >
              {opt.label || opt.type}
            </button>
          ))}
        </div>
        <input
          type="number"
          min="0"
          step="1"
          className="fe2-sticky__fee-input fe2-ltr"
          value={value}
          aria-label="ערך עמלת טיפול"
          onChange={(e) => {
            const n = Number(e.target.value);
            onHandlingFeeChange({
              type,
              value: Number.isFinite(n) && n >= 0 ? n : 0,
            });
          }}
        />
      </div>
    </section>
  );
}

/**
 * Sticky booking summary — presentation only.
 * Renders offer.bookingSummary ViewModel. No price math.
 */
export default function StickyBookingSummary({
  offer,
  search,
  onContinue,
  onBack,
  continueLabel = "המשך לפרטי נוסעים",
  backLabel = "חזרה לתוצאות",
  continueDisabled = false,
  handlingFee = null,
  onHandlingFeeChange = null,
}) {
  const { airlines } = useAppContext();
  const airlineCode = offer?.airline?.code || "";
  const airlineIdentity = useMemo(
    () =>
      resolveReviewAirlineIdentity({
        airlines,
        code: airlineCode,
        fallbackName: offer?.airline?.name,
      }),
    [airlines, airlineCode, offer?.airline?.name]
  );
  if (!offer) return null;
  const s = offer.bookingSummary;
  if (!s) return null;
  const fareFamily = s.fare?.family || "";
  const fareFamilyIsCode = labelIsAirlineCode(fareFamily, airlineCode);
  const showAirlineIdentity =
    Boolean(airlineIdentity.name) &&
    !labelIsAirlineCode(airlineIdentity.name, airlineCode) &&
    (labelIsAirlineCode(offer?.airline?.name, airlineCode) || fareFamilyIsCode);

  const extras = s.extras?.items || [];
  const hasExtras = extras.length > 0;
  const showRounding = Boolean(s.totals?.roundingAdjustmentLabel);

  return (
    <aside className="fe2-sticky" data-fe2-sticky-summary="1">
      <h2 className="fe2-sticky__title">סיכום הזמנה</h2>

      <div className="fe2-sticky__route">
        <strong className="fe2-sticky__route-main fe2-ltr">
          {search?.origin} → {search?.destination}
        </strong>
        <span className="fe2-sticky__route-meta fe2-ltr">
          {search?.departDateLabel?.slice(0, 5)}–{search?.returnDateLabel?.slice(0, 5)}
        </span>
        <span className="fe2-sticky__route-meta">{search?.passengersLabel}</span>
      </div>

      {s.flights ? (
        <section className="fe2-sticky__section">
          <h3 className="fe2-sticky__section-title">{s.flights.title}</h3>
          <MoneyRow
            label={s.flights.lineLabel}
            value={s.flights.amountLabel}
          />
        </section>
      ) : null}

      {s.baggage ? (
        <section className="fe2-sticky__section">
          {s.baggage.breakdown?.groups?.length ? null : (
            <h3 className="fe2-sticky__section-title">{s.baggage.title}</h3>
          )}
          {(s.baggage.items || []).map((item) => (
            <MoneyRow
              key={`${item.label}-${item.valueLabel}`}
              label={item.label}
              secondary={item.secondary || null}
              value={item.valueLabel}
              tone={item.tone || "default"}
              ltrValue={Boolean(item.ltr)}
            />
          ))}
          {s.baggage.extraLine ? (
            <MoneyRow
              label={s.baggage.extraLine.label}
              value={s.baggage.extraLine.amountLabel}
              tone="muted"
            />
          ) : null}
          <BaggageBreakdown breakdown={s.baggage.breakdown} />
        </section>
      ) : null}

      {s.extras ? (
        <section className="fe2-sticky__section">
          {hasExtras ? (
            <>
              <h3 className="fe2-sticky__section-title">{s.extras.title}</h3>
              {extras.map((item) => (
                <MoneyRow
                  key={item.label}
                  label={item.label}
                  value={item.amountLabel}
                  tone="paid"
                />
              ))}
            </>
          ) : (
            <MoneyRow
              label={s.extras.emptyLabel || "תוספות"}
              value={s.extras.emptyAmountLabel ?? ""}
              tone="muted"
            />
          )}
        </section>
      ) : null}

      {s.fare ? (
        <section className="fe2-sticky__section">
          <h3 className="fe2-sticky__section-title">{s.fare.title}</h3>
          {showAirlineIdentity ? (
            <p className="fe2-sticky__fare-line fe2-sticky__airline">
              {airlineIdentity.logoSrc ? (
                <img
                  src={airlineIdentity.logoSrc}
                  alt=""
                  className="fe2-sticky__airline-logo"
                  decoding="async"
                />
              ) : null}
              <span className="fe2-ltr" data-fe2-airline-name="">
                {airlineIdentity.name}
              </span>
            </p>
          ) : null}
          {fareFamily && !fareFamilyIsCode ? (
            <p className="fe2-sticky__fare-line">{fareFamily}</p>
          ) : null}
          {s.fare.cabin ? (
            <p className="fe2-sticky__fare-line fe2-sticky__fare-line--muted">
              {s.fare.cabin}
            </p>
          ) : null}
        </section>
      ) : null}

      {s.sourceTotal || s.baggageUsdTotal ? (
        <section className="fe2-sticky__section">
          {s.sourceTotal ? (
            <MoneyRow
              label={s.sourceTotal.title}
              value={s.sourceTotal.amountLabel}
            />
          ) : null}
          {s.baggageUsdTotal ? (
            <MoneyRow
              label={s.baggageUsdTotal.title}
              value={s.baggageUsdTotal.amountLabel}
            />
          ) : null}
        </section>
      ) : null}

      {s.exchangeRate ? (
        <section className="fe2-sticky__section fe2-sticky__section--fx">
          <h3 className="fe2-sticky__section-title">{s.exchangeRate.title}</h3>
          <p className="fe2-sticky__fx-rate fe2-ltr">{s.exchangeRate.rateLabel}</p>
          <p className="fe2-sticky__fx-note">{s.exchangeRate.note}</p>
          {s.exchangeRate.mock ? (
            <span className="fe2-sticky__mock-tag">mock</span>
          ) : null}
        </section>
      ) : null}

      {s.totals || s.commission ? (
        <section className="fe2-sticky__section fe2-sticky__section--totals">
          {s.totals ? (
            <MoneyRow
              label={s.totals.calculatedLabel || "סה״כ בשקלים"}
              value={s.totals.calculatedTotalLabel}
            />
          ) : null}
          {showRounding ? (
            <MoneyRow
              label={s.totals.roundingLabel || "עיגול"}
              value={s.totals.roundingAdjustmentLabel}
              tone="muted"
            />
          ) : null}
          {s.totals?.baggageIlsAmountLabel ? (
            <MoneyRow
              label={s.totals.baggageIlsLabel || "כבודה בשקלים"}
              value={s.totals.baggageIlsAmountLabel}
            />
          ) : null}
          {s.commission ? (
            <MoneyRow
              label={s.commission.label}
              value={s.commission.valueLabel}
            />
          ) : null}
          {s.totals ? (
            <div className="fe2-sticky__payable">
              <span className="fe2-sticky__payable-label">
                {s.totals.payableLabel || "לתשלום"}
              </span>
              <strong className="fe2-sticky__payable-amount fe2-ltr">
                {s.totals.payableTotalLabel}
              </strong>
            </div>
          ) : null}
        </section>
      ) : null}

      <HandlingFeeControls
        handlingFee={handlingFee}
        onHandlingFeeChange={onHandlingFeeChange}
      />

      <button
        type="button"
        className="fe2-sticky__cta"
        onClick={onContinue}
        disabled={continueDisabled}
      >
        {continueLabel}
      </button>
      <button type="button" className="fe2-sticky__secondary" onClick={onBack}>
        {backLabel}
      </button>
    </aside>
  );
}
